import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useGetSalesHistoryQuery } from 'services/api/sales';
import { setPage, selectTabFilters } from 'services/salesHistorySlice/salesHistorySlice';
import HistoryPage from '../components/HistoryPage';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #666;
  font-size: 16px;
`;

const TAB_FILTERS: Record<string, Record<string, string>> = {
  sold: { status: 'awarded' },
  waiting_on_title: { title_status: 'waiting_on_title' }
};

const HistoryTab = ({ tabKey, active, skip }: { tabKey: string; active: boolean; skip: boolean }) => {
  const dispatch = useDispatch();
  const { pages, perPage } = useSelector((state: any) => state.salesHistory);
  const filters = useSelector((state: any) => selectTabFilters(state, tabKey));
  const pageCount = pages[tabKey] || 1;
  const tabFilters = TAB_FILTERS[tabKey] ?? {};

  // Query the last page to detect loading state & determine if more pages exist
  const { data: lastPageData, isFetching } = useGetSalesHistoryQuery({
    limit: perPage,
    offset: pageCount,
    ...filters,
    ...tabFilters
  }, { skip });

  // Use page 1 data for total count (most reliably cached)
  const { data: firstPageData } = useGetSalesHistoryQuery({
    limit: perPage,
    offset: 1,
    ...filters,
    ...tabFilters
  }, { skip });

  const totalCount = Number(firstPageData?.count ?? lastPageData?.count ?? 0);
  const canLoadMore = perPage * pageCount < totalCount;
  const isEmpty = !isFetching && totalCount === 0;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && active && canLoadMore && !isFetching) {
          dispatch(setPage({ tab: tabKey, page: pageCount + 1 }));
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, isFetching, pageCount, active, dispatch, tabKey]);

  if (isEmpty) {
    return (
      <EmptyState>
        Once you list your first vehicle, you will see the sale status of that vehicle in this section.
      </EmptyState>
    );
  }

  const pageNumbers = Array.from({ length: pageCount }, (_, i) => i + 1);

  return (
    <>
      <ListWrapper>
        {pageNumbers.map(page => (
          <HistoryPage key={page} page={page} tabKey={tabKey} />
        ))}
      </ListWrapper>
      <LoaderWrapper>
        <>
          {isFetching && <CircularProgress size={32} />}
          <Sentinel ref={sentinelRef} />
        </>
      </LoaderWrapper>
    </>
  );
};

export default HistoryTab;
